import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { CONTACT_EMAIL, LOGO_URL } from "@/pages/Landing";

const COMPANY_NAME = "Buzas Indústria e Comércio Ltda";
const LAST_UPDATED = "8 de outubro de 2026";

const EmailLink = () => (
  <a
    href={`mailto:${CONTACT_EMAIL}`}
    className="font-semibold text-[color:var(--buzas-blue)] underline underline-offset-4"
  >
    {CONTACT_EMAIL}
  </a>
);

const Section = ({ title, children }) => (
  <section className="mt-10">
    <h2 className="font-display text-xl font-semibold text-[color:var(--buzas-blue-ink)] sm:text-2xl">
      {title}
    </h2>
    <div className="mt-4 space-y-4 text-base leading-relaxed text-slate-600">
      {children}
    </div>
  </section>
);

export default function PrivacyPolicy() {
  useEffect(() => {
    document.documentElement.scrollTop = 0;
  }, []);

  return (
    <div data-testid="privacy-policy-page" className="min-h-screen bg-white">
      <header className="border-b border-slate-200">
        <div className="mx-auto flex max-w-3xl items-center justify-between px-4 py-4 sm:px-6">
          <Link to="/" data-testid="privacy-logo-link">
            <img
              src={LOGO_URL}
              alt="Buzas"
              className="h-12 w-auto object-contain"
            />
          </Link>
          <Link
            to="/"
            data-testid="privacy-back-link"
            className="inline-flex items-center gap-2 text-sm font-semibold text-[color:var(--buzas-blue)] hover:text-[color:var(--buzas-blue-ink)]"
          >
            <ArrowLeft className="h-4 w-4" />
            Voltar ao site
          </Link>
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-4 pb-40 pt-12 sm:px-6 sm:pt-16">
        <h1 className="font-display text-3xl font-semibold leading-tight text-[color:var(--buzas-blue-ink)] sm:text-4xl">
          Política de Privacidade
        </h1>
        <p className="mt-3 text-sm text-slate-500">
          Última atualização: {LAST_UPDATED}
        </p>

        <div className="mt-8 space-y-4 text-base leading-relaxed text-slate-600">
          <p>
            A {COMPANY_NAME} ("Buzas") respeita a sua privacidade e trata os
            seus dados pessoais de acordo com a Lei Geral de Proteção de Dados
            Pessoais (Lei nº 13.709/2018 — LGPD). Esta política explica quais
            dados coletamos neste site, para que os utilizamos e quais são os
            seus direitos.
          </p>
        </div>

        <Section title="1. Quem é o controlador dos dados">
          <p>
            O controlador dos dados pessoais tratados por meio deste site é a{" "}
            {COMPANY_NAME}. Para qualquer assunto relacionado a privacidade e
            proteção de dados, entre em contato pelo e-mail <EmailLink />.
          </p>
        </Section>

        <Section title="2. Quais dados coletamos">
          <p>Coletamos apenas os dados necessários para atender você:</p>
          <ul className="list-disc space-y-2 pl-6">
            <li>
              <strong>Dados informados por você</strong> no formulário de
              contato: nome, e-mail, telefone e a mensagem que você escrever.
            </li>
            <li>
              <strong>Dados de contato por outros canais</strong>, como
              WhatsApp e e-mail, quando você decide falar conosco por eles.
            </li>
            <li>
              <strong>Dados de navegação</strong>, como endereço IP, tipo de
              dispositivo e navegador, páginas visitadas e data e hora de
              acesso, registrados automaticamente pelos servidores que
              hospedam o site.
            </li>
          </ul>
        </Section>

        <Section title="3. Para que usamos os seus dados">
          <ul className="list-disc space-y-2 pl-6">
            <li>
              Responder a pedidos de contato e elaborar orçamentos técnicos.
            </li>
            <li>
              Dar andamento a negociações e à relação comercial com você ou
              com a sua empresa.
            </li>
            <li>Manter o site funcionando com segurança e prevenir fraudes.</li>
            <li>Cumprir obrigações legais e regulatórias.</li>
          </ul>
        </Section>

        <Section title="4. Bases legais">
          <p>
            Tratamos dados pessoais com fundamento nas bases legais previstas
            na LGPD, em especial: a execução de procedimentos preliminares a
            um contrato a pedido do titular, o legítimo interesse da Buzas em
            atender e se relacionar com clientes, o cumprimento de obrigação
            legal ou regulatória e, quando aplicável, o seu consentimento.
          </p>
        </Section>

        <Section title="5. Com quem compartilhamos">
          <p>
            A Buzas não vende dados pessoais. Os dados podem ser compartilhados
            apenas com prestadores de serviço que apoiam a operação do site e
            o atendimento comercial, como serviços de hospedagem, e-mail e
            mensagens, sempre no limite do necessário, e com autoridades
            públicas quando houver obrigação legal ou ordem judicial.
          </p>
          <p>
            Alguns desses prestadores podem estar localizados fora do Brasil.
            Nesses casos, a transferência internacional ocorre de acordo com
            as hipóteses previstas na LGPD.
          </p>
        </Section>

        <Section title="6. Cookies e armazenamento local">
          <p>
            Este site utiliza armazenamento local do navegador para funções
            essenciais, como registrar a sua escolha no aviso de privacidade.
            Cookies e tecnologias não essenciais, como as de medição de
            audiência e publicidade, somente são utilizados se você aceitar
            no aviso exibido no seu primeiro acesso.
          </p>
          <p>
            Você pode apagar os cookies e os dados armazenados a qualquer
            momento nas configurações do seu navegador. Ao fazer isso, o aviso
            de privacidade voltará a ser exibido.
          </p>
        </Section>

        <Section title="7. Por quanto tempo guardamos os dados">
          <p>
            Mantemos os dados pessoais pelo tempo necessário para cumprir as
            finalidades descritas nesta política, respeitando os prazos legais
            de guarda. Depois disso, os dados são eliminados ou anonimizados.
          </p>
        </Section>

        <Section title="8. Seus direitos">
          <p>Nos termos da LGPD, você pode solicitar a qualquer momento:</p>
          <ul className="list-disc space-y-2 pl-6">
            <li>a confirmação da existência de tratamento e o acesso aos dados;</li>
            <li>a correção de dados incompletos, inexatos ou desatualizados;</li>
            <li>
              a anonimização, o bloqueio ou a eliminação de dados
              desnecessários ou excessivos;
            </li>
            <li>a portabilidade dos dados;</li>
            <li>
              informações sobre com quem os seus dados foram compartilhados;
            </li>
            <li>
              a revogação do consentimento e a eliminação dos dados tratados
              com base nele.
            </li>
          </ul>
          <p>
            Para exercer esses direitos, escreva para <EmailLink />. Você
            também pode apresentar reclamação à Autoridade Nacional de
            Proteção de Dados (ANPD).
          </p>
        </Section>

        <Section title="9. Segurança">
          <p>
            Adotamos medidas técnicas e administrativas razoáveis para
            proteger os dados pessoais contra acessos não autorizados, perda,
            alteração ou divulgação indevida. Nenhum sistema é totalmente
            imune a riscos, e por isso revisamos nossas práticas
            continuamente.
          </p>
        </Section>

        <Section title="10. Alterações desta política">
          <p>
            Esta política pode ser atualizada para refletir mudanças no site
            ou na legislação. A versão vigente estará sempre disponível nesta
            página, com a data da última atualização indicada no topo.
          </p>
        </Section>

        <Section title="11. Contato">
          <p>
            Dúvidas sobre esta política ou sobre o tratamento dos seus dados
            pessoais podem ser enviadas para <EmailLink />.
          </p>
        </Section>
      </main>
    </div>
  );
}
